import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { FocusTool, Icon, Tool } from '../basic';
import { Breakpoints, intl, useBreakpoint } from '../../lib';
import { AccessibleClickableContainer } from '../accessibility';

export type ListDetailItem = {
  id: string;
  title: string | (() => React.JSX.Element);
  link: () => React.JSX.Element;
  render: () => React.JSX.Element;
};

export type ListDetailProps = {
  title: string;
  items: ListDetailItem[];
  mobileItemPreview?: React.JSX.Element;
  unselectedState?: React.JSX.Element;
  listFooter?: React.JSX.Element;
  onItemSelected?: (item: ListDetailItem) => void;
  initialSelectedItemId?: string;
  className?: string;
  contentAriaRole?: 'region' | 'form';
};

/**
 * A master-detail (list-detail) layout component that displays a list of selectable items
 * and their corresponding details in a responsive two-pane interface.
 *
 * ## When to use
 * - Displaying detail content for items selected from a list (e.g., document viewer, appointment details)
 * - Simple selection state where the right panel shows details of the left panel's selected item
 * - Accessibility needs are met via `aria-expanded`/`aria-controls` pattern
 *
 * ## When NOT to use
 * - If the right panel represents distinct, peer-level views or forms → use `VerticalTabs` instead
 *   (VerticalTabs provides full ARIA tabs pattern with keyboard navigation: arrow keys, Home, End)
 *
 * ## Accessibility
 * - Selection state exposed via `aria-expanded` on list items
 * - Each item controls a region via `aria-controls`
 * - Focus automatically moves to content panel after selection (mobile and desktop)
 * - Mobile: back button returns to list view
 *
 * @param title the title of the list shown on the top-left
 * @param items the items to be displayed in the list selection
 * @param mobileItemPreview an element that is shown as preview for link items on mobile
 * @param unselectedState a component to show when no item is selected
 * @param listFooter additional content rendered after the item list in the left panel
 * @param onItemSelected a callback function that is called when an item is selected
 * @param initialSelectedItemId the id of the item to be initially selected
 * @param contentAriaRole the ARIA role to apply to the content area (default is "region")
 * @param props additional props to pass to the root element
 */
export function ListDetail({
  title,
  items,
  mobileItemPreview,
  unselectedState,
  listFooter,
  onItemSelected,
  initialSelectedItemId,
  contentAriaRole = 'region',
  ...props
}: Readonly<ListDetailProps>): React.JSX.Element {
  const detailViewAriaDescriptionId = useId();
  const isMobileView = useBreakpoint(Breakpoints.MOBILE);
  const [selectedItem, setSelectedItem] = React.useState<
    ListDetailItem | undefined
  >(items.find(item => item.id === initialSelectedItemId));
  const [previewImageVisible, setPreviewImageVisible] = useState(true);
  const itemLinks = React.useMemo(() => {
    return items.map((item: ListDetailItem) => (
      <AccessibleClickableContainer
        key={'list-detail-item-' + item.id}
        className={
          'ehs-list-detail-item' +
          (selectedItem?.id === item.id ? ' selected' : '')
        }
        onClick={() => {
          setSelectedItem(item);
          if (onItemSelected) {
            onItemSelected(item);
          }
        }}
        aria-expanded={selectedItem?.id === item.id}
        aria-controls={detailViewAriaDescriptionId}
      >
        <div className="ehs-list-detail-item-link">{item.link()}</div>
        <Icon iconName="arrow-right" aria-label={undefined} />
      </AccessibleClickableContainer>
    ));
  }, [detailViewAriaDescriptionId, items, onItemSelected, selectedItem?.id]);

  const mobileItemPreviewRef = useCallback(
    (element: HTMLDivElement) => {
      if (!element || !isMobileView) {
        setPreviewImageVisible(false);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          setPreviewImageVisible(entry.isIntersecting);
        },
        { threshold: 0 }
      );

      observer.observe(element);

      return () => {
        observer.disconnect();
      };
    },
    [isMobileView]
  );
  const detailContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Move keyboard/screen-reader focus to the controlled panel after card selection.
    detailContainerRef.current?.focus();
  }, [selectedItem]);

  const ariaLabelForm = useId();
  return (
    <div
      {...props}
      className={
        'ehs-list-detail' + (props.className ? ` ${props.className}` : '')
      }
    >
      {(!isMobileView || selectedItem === undefined) && items.length > 0 && (
        <div className="ehs-list-detail-left">
          {isMobileView && mobileItemPreview && (
            <div
              ref={mobileItemPreviewRef}
              className="ehs-list-detail-left-preview"
            >
              {mobileItemPreview}
            </div>
          )}
          <div className="ehs-list-detail-left-header-container">
            <div
              className={
                'ehs-list-detail-left-header' +
                (mobileItemPreview ? ' preview-available' : '') +
                (previewImageVisible ? ' preview-visible' : '')
              }
            >
              <h2 className="sh-font">{title}</h2>
            </div>
          </div>
          {itemLinks}
          {listFooter}
        </div>
      )}
      {(!isMobileView || selectedItem !== undefined || items.length === 0) && (
        <div
          className={
            'ehs-list-detail-right' + (selectedItem ? '' : ' empty-state')
          }
          id={detailViewAriaDescriptionId}
        >
          {selectedItem ? (
            <div
              className="ehs-list-detail-item-control-container"
              role={contentAriaRole}
              aria-labelledby={ariaLabelForm}
              ref={detailContainerRef}
              tabIndex={-1}
            >
              <div className="ehs-list-detail-item-header">
                {isMobileView && (
                  <Tool
                    icon="arrow-left"
                    className="ehs-list-detail-item-header-spacer"
                    onClick={() => setSelectedItem(undefined)}
                    tooltip={intl('view.button.back')}
                  />
                )}
                {typeof selectedItem.title === 'string' ? (
                  <h3 className="sh-font" id={ariaLabelForm}>
                    {selectedItem.title}
                  </h3>
                ) : (
                  <div id={ariaLabelForm}>{selectedItem.title()}</div>
                )}
                {!isMobileView && (
                  <FocusTool
                    selector={'.ehs-list-detail-item.selected'}
                    icon="skip-link-left"
                    tooltip={intl(
                      'appointmentSchedulingPortlet.focusto.documentlist'
                    )}
                  />
                )}
                {isMobileView && (
                  <div className="ehs-list-detail-item-header-spacer"></div>
                )}
              </div>
              <div className="ehs-list-detail-item-control">
                {selectedItem.render()}
              </div>
            </div>
          ) : (
            unselectedState
          )}
        </div>
      )}
    </div>
  );
}
